import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ButtonColor, ButtonSize } from '@predictiveindex/tapestry';
import { ModuleName } from '../../../core-data-model/ModuleAccess/ModuleAccess.model';
import { UpgradeAccountService } from '../../../core-ui/upgrade-account/upgrade-account.service';
import { FeatureFlags, FeatureFlagsService } from '../../../system-services';
import { TranslationService } from '../../../translations';

export enum TemplateType {
    Unknown,
    Paid,
    TimeTrialExpired,
    NoUserAccess
}

@Component({
    selector: 'internal-activations-error-template',
    templateUrl: './internal-activations-error-template.component.html',
    styleUrls: ['./internal-activations-error-template.component.less'],
    standalone: false
})
export class InternalActivationsErrorTemplateComponent implements OnInit {
    @Input() templateType: TemplateType = TemplateType.Unknown;

    imgSrc: string;
    imgAlt: string;
    description: string;
    headerLabel: string;
    secondaryDescription: string;
    upgradeAccountButtonText: string;
    isUpgradeAccountButtonVisible = false;
    isUpgradeAccountButtonEnabled = false;
    isUpgradeAccountButtonFeatureEnabled = false;
    softwareModule: string;
    additionalDetailsHtml: string;
    additionalDetailsClass: string;
    ButtonSize = ButtonSize;
    ButtonColor = ButtonColor;

    hireAdditionalDetailsLink =
        'https://www.predictiveindex.com/unlock-your-full-potential/';

    constructor(
        private TR: TranslationService,
        private route: ActivatedRoute,
        private accountUpgradeService: UpgradeAccountService,
        private featureFlagService: FeatureFlagsService
    ) {}

    ngOnInit() {
        this.route.paramMap.subscribe((params) => {
            this.softwareModule = params.get('module')?.toLowerCase();

            this.headerLabel = this.setHeaderLabel();
            this.upgradeAccountButtonText = this.setUpgradeAccountButtonText();
        });

        this.isUpgradeAccountButtonFeatureEnabled =
            this.featureFlagService.getByFlag(
                FeatureFlags.internalActivationsUpgradeAccountButton
            );

        switch (this.templateType) {
            case TemplateType.Paid:
                this.setPaidTemplateData();
                break;
            case TemplateType.TimeTrialExpired:
                this.setTimeTrialExpiredTemplateData();
                break;
            case TemplateType.NoUserAccess:
                this.setNoUserAccessTemplateData();
                break;
            default:
                this.setPaidTemplateData();
                break;
        }
    }

    public async upgradeAccountClicked(): Promise<void> {
        if (!this.isUpgradeAccountButtonFeatureEnabled) {
            return;
        }
        this.toggleUpgradeAccountButton(true, false);
        await this.accountUpgradeService.upgradeAccount(true);
        this.toggleUpgradeAccountButton(true, true);
    }

    private setHeaderLabel(): string {
        switch (this.softwareModule) {
            case 'hire':
                return 'hireHero.jobCenter.yourJobsDashboard';
            case 'diagnose':
                return 'diagnose.surveycenter.title.surveyCenter';
            default:
                return `label.common-terms.${this.softwareModule}`;
        }
    }

    private setUpgradeAccountButtonText(): string {
        if (this.softwareModule !== 'diagnose') {
            return 'internal-activations.time-trial-expired.upgrade-account-button-text';
        }

        return 'internal-activations.time-trial-expired.upgrade-account-button.diagnose';
    }

    private setPaidTemplateData(): void {
        this.imgSrc = `/platformAssets/internalActivations/${this.softwareModule}-paid.svg`;
        this.imgAlt = this.TR.key(
            `internal-activations.paid.image.alt-text.${this.softwareModule}`
        );
        this.description = this.TR.key(
            `internal-activations.paid.description.${this.softwareModule}`
        );
        this.secondaryDescription = this.TR.key(
            `internal-activations.paid.secondary-description.${this.softwareModule}`
        );
        this.toggleUpgradeAccountButton(false, false);
    }

    private setTimeTrialExpiredTemplateData(): void {
        this.imgSrc = `/platformAssets/internalActivations/time-trial-expired.svg`;
        this.imgAlt = this.TR.key(
            `internal-activations.time-trial-expired.image.alt-text.${this.softwareModule}`
        );
        this.description = this.TR.key(
            `internal-activations.time-trial-expired.description.${this.softwareModule}`
        );
        this.secondaryDescription = this.TR.key(
            `internal-activations.time-trial-expired.secondary-description.${this.softwareModule}`
        );
        this.setAdditionalDetails(this.softwareModule);
        this.toggleUpgradeAccountButton(
            this.isUpgradeAccountButtonFeatureEnabled,
            this.isUpgradeAccountButtonFeatureEnabled
        );
    }

    private setNoUserAccessTemplateData(): void {
        if (this.softwareModule === 'perform') {
            this.imgSrc = `/platformAssets/internalActivations/perform4.svg`;
            this.description = this.TR.key(
                'perform.no-access.description-title'
            );
            this.secondaryDescription = this.TR.key(
                'perform.no-access.description'
            );
            this.toggleUpgradeAccountButton(false, false);
        }
    }

    private toggleUpgradeAccountButton(
        isVisible: boolean,
        isEnabled: boolean
    ): void {
        this.isUpgradeAccountButtonVisible = isVisible;
        this.isUpgradeAccountButtonEnabled = isEnabled;
    }

    private setAdditionalDetails(module: string) {
        if (module !== ModuleName.Hire.toLowerCase()) {
            return;
        }
        this.additionalDetailsClass = 'hire-additional-details';
        this.additionalDetailsHtml = this.TR.keyFormatter(
            'timeTrial.hireHero.additionalDetails',
            this.hireAdditionalDetailsLink
        );
    }
}
